// src/borrowerApi.js
// ─────────────────────────────────────────────────────────────
// Thin client for the borrower-portal serverless endpoints. Everything
// the borrower-facing UI does against the DB goes through these helpers
// instead of hitting Supabase with the anon key directly.
//
// The session JWT lives in localStorage under a dedicated key so it's
// isolated from Supabase's session storage (used by admin/guest auth).
// Logout clears the token; expired tokens produce a 401 which the caller
// can catch to bounce the user back to the login screen.
// ─────────────────────────────────────────────────────────────

const TOKEN_KEY = "loanflow_borrower_token";
const LOAN_CACHE_KEY = "loanflow_borrower_loan_cache";

// Tokens are stored in localStorage. That's XSS-vulnerable in theory, but
// the CSP, sanitized DocReview rendering, and absence of dangerouslySetInnerHTML
// elsewhere keep the attack surface small for V1. Upgrade to an httpOnly
// cookie in Phase 1B Part 2 when we add signed-upload-url support.
export function setToken(t) {
  try { localStorage.setItem(TOKEN_KEY, t); } catch {}
}
export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LOAN_CACHE_KEY);
  } catch {}
}
export function cacheLoan(loan) {
  try { localStorage.setItem(LOAN_CACHE_KEY, JSON.stringify(loan)); } catch {}
}
export function getCachedLoan() {
  try {
    const s = localStorage.getItem(LOAN_CACHE_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

async function parseJsonSafe(resp) {
  try { return await resp.json(); } catch { return null; }
}

// Borrower login. Returns { ok: true, token, loan } on success, or
// { ok: false, error } on failure. Never throws — the UI just needs to
// know which flow to render next.
export async function login(code) {
  if (!code) return { ok: false, error: "code_required" };
  try {
    const resp = await fetch("/api/borrower/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    const data = await parseJsonSafe(resp);
    if (!resp.ok) return { ok: false, error: (data && data.error) || "login_failed" };
    if (!data || !data.token) return { ok: false, error: "empty_response" };
    setToken(data.token);
    if (data.loan) cacheLoan(data.loan);
    return { ok: true, token: data.token, loan: data.loan };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Fetch the authenticated borrower's loan. Returns { ok, loan } or
// { ok: false, error }. 401 → session expired, caller should force logout.
export async function fetchLoan() {
  const token = getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/loan", {
      headers: { Authorization: "Bearer " + token },
    });
    const data = await parseJsonSafe(resp);
    if (resp.status === 401) {
      clearToken();
      return { ok: false, error: "expired" };
    }
    if (!resp.ok) return { ok: false, error: (data && data.error) || "fetch_failed" };
    if (data && data.loan) cacheLoan(data.loan);
    return { ok: true, loan: data && data.loan };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Submit a callback request.
export async function submitCallback({ phone, preferredTime, note, prefContact, bestTimeChips }) {
  const token = getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/callback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ phone, preferredTime, note, prefContact, bestTimeChips }),
    });
    const data = await parseJsonSafe(resp);
    if (resp.status === 401) { clearToken(); return { ok: false, error: "expired" }; }
    if (!resp.ok) return { ok: false, error: (data && data.error) || "callback_failed" };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Fire-and-forget notification that a borrower uploaded one or more files.
// The endpoint emails the admin. Network errors are swallowed so the upload
// UX never blocks on this.
export async function notifyUpload({ fileNames }) {
  const token = getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/notify-upload", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ fileNames: fileNames || [] }),
    });
    if (!resp.ok) return { ok: false, error: "notify_failed" };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "network_error" };
  }
}

// Statement-info submission. The endpoint persists statement_data on the
// loan, records the photo upload entry (photoPath from the signed-upload
// flow), adds the review task, and emails the admin.
export async function notifyStatement({ servicer, balance, rate, principalLimit, loc, youngestDob, goals, hasPhoto, photoPath, photoName, photoType }) {
  const token = getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/notify-statement", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ servicer, balance, rate, principalLimit, loc, youngestDob, goals, hasPhoto: !!hasPhoto, photoPath, photoName, photoType }),
    });
    // A 24h-expired session returns 401. Surface it distinctly (and clear the
    // dead token) so the portal can tell the borrower to log in again instead
    // of silently dropping the submission and claiming success.
    if (resp.status === 401) { clearToken(); return { ok: false, error: "expired" }; }
    if (!resp.ok) return { ok: false, error: "notify_failed" };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "network_error" };
  }
}

// ── Document storage (api/borrower/docs action router) ──
// Shared POST helper: borrower-token actions pass authToken=getToken();
// admin actions pass the admin's Supabase access token instead.
async function docsAction(payload, authToken) {
  const token = authToken || getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/docs", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(payload || {}),
    });
    const data = await parseJsonSafe(resp);
    if (resp.status === 401 && !authToken) { clearToken(); return { ok: false, error: "expired" }; }
    if (!resp.ok) return { ok: false, error: (data && data.error) || "docs_action_failed" };
    return Object.assign({ ok: true }, data || {});
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Borrower: get a signed Storage upload slot. Returns { ok, path, token }.
export function getUploadUrl({ fileName, contentType }) {
  return docsAction({ action: "upload-url", fileName, contentType });
}
// Borrower: record a completed storage upload on the loan (server-side write
// + admin email). Returns { ok, upload }.
export function confirmUpload({ path, fileName, size, fileType }) {
  return docsAction({ action: "confirm-upload", path, fileName, size, fileType });
}
// Borrower: signed download URL for one of their own stored docs.
export function getDocUrl({ path }) {
  return docsAction({ action: "doc-url", path });
}
// Borrower: persist the counseling confirmation server-side.
export function submitCounseling({ date, time }) {
  return docsAction({ action: "counseling", date, time });
}
// ADMIN: signed download URL for any stored doc (Loan Detail viewer).
export function adminGetDocUrl({ accessToken, path }) {
  return docsAction({ action: "doc-url", path }, accessToken);
}

// Borrower testimonial submission (funded stage). Uses the borrower session
// JWT. Server emails Reece + saves it on the loan. Returns { ok } / { ok:false }.
export async function submitTestimonial({ text, rating }) {
  const token = getToken();
  if (!token) return { ok: false, error: "no_session" };
  try {
    const resp = await fetch("/api/borrower/testimonial", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ text, rating: rating || null }),
    });
    const data = await parseJsonSafe(resp);
    if (resp.status === 401) { clearToken(); return { ok: false, error: "expired" }; }
    if (!resp.ok) return { ok: false, error: (data && data.error) || "testimonial_failed" };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// ADMIN: send a borrower their portal-access email (code + magic link +
// document checklist). `accessToken` is the admin's Supabase session token;
// the server verifies it before sending. Returns { ok } / { ok:false, error }.
export async function sendAccess({ accessToken, loanId, email, code, borrowerName, note, sections }) {
  if (!accessToken) return { ok: false, error: "no_admin_session" };
  try {
    const resp = await fetch("/api/borrower/send-access", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
      body: JSON.stringify({ loanId, email, code, borrowerName, note, sections }),
    });
    const data = await parseJsonSafe(resp);
    if (!resp.ok) return { ok: false, error: (data && data.error) || "send_failed" };
    return { ok: true, id: data && data.id };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Login-screen intake form submission. No session required (it's the
// means by which a prospect announces themselves).
export async function submitIntake(payload) {
  try {
    const resp = await fetch("/api/intake", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
    const data = await parseJsonSafe(resp);
    if (!resp.ok) return { ok: false, error: (data && data.error) || "intake_failed" };
    return { ok: true, id: data && data.id };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Public (no session): request a signed Storage upload slot for an intake
// statement file. The server scopes the path to intake/<leadId>/ and generates
// the filename. Returns { ok, path, token } / { ok:false, error }. The caller
// then uploads the file bytes directly to Storage with uploadToSignedUrl —
// bypassing the ~4.5MB serverless body cap so large PDFs go through.
export async function getIntakeUploadUrl({ leadId, fileName, contentType }) {
  try {
    const resp = await fetch("/api/intake", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "upload-url", leadId, fileName, contentType }),
    });
    const data = await parseJsonSafe(resp);
    if (!resp.ok) return { ok: false, error: (data && data.error) || "upload_url_failed" };
    return { ok: true, path: data && data.path, token: data && data.token };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}

// Login-screen marketing opt-out submission. No session. Server enforces
// name + address as required and creates an admin-actionable record.
export async function submitOptout(payload) {
  try {
    const resp = await fetch("/api/optout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
    const data = await parseJsonSafe(resp);
    if (!resp.ok) return { ok: false, error: (data && data.error) || "optout_failed" };
    return { ok: true, id: data && data.id };
  } catch (e) {
    return { ok: false, error: "network_error", detail: e && e.message };
  }
}
